/**
 * Auth API client - 包装 /v1/auth/* + /v1/users + /v1/tenant/me.
 *
 * Token 存 localStorage:
 *   cnc.access_token   (Bearer 头)
 *   cnc.refresh_token  (refresh 接口用)
 *
 * 跟 client.ts 一起工作: 请求自动加 Authorization · 401 自动清 token 跳 /login
 */
import { apiClient } from "./client";

// ─── 类型定义 ────────────────────────────────────────────

export type UserRole =
  | "programmer"
  | "process_lead"
  | "plant_manager"
  | "quality"
  | "operator"
  | "it_admin"
  | "auditor"
  | "platform_ops";

export interface TenantInfo {
  id: string;
  name: string;
  code: string;
  plan_tier: string;
  deployment: string;
  is_active: boolean;
  industry?: string | null;
  region?: string | null;
}

export interface UserInfo {
  id: string;
  employee_id: string;
  name: string;
  email: string | null;
  /** 手机号 · E.164(+86…)· 老的邮箱账号没有(c178) */
  phone?: string | null;
  role: UserRole;
  is_active: boolean;
  tenant: TenantInfo;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: "bearer";
  expires_in: number;
}

export interface RegisterRequest {
  /** 开通邀请码 · 平台运营发放 · 一码一用(后端 c177) */
  invite_code: string;
  tenant_name: string;
  /** 管理员手机号 · 登录用 · 中国大陆(后端规范化成 +86…) */
  phone: string;
  /** 邮箱 · 选填(填了也能用它登录);不填就别发这个字段 */
  email?: string;
  admin_name: string;
  password: string;
}

export interface LoginRequest {
  /** 手机号或邮箱(c178 · 后端判断是哪一种) */
  account: string;
  password: string;
}

export interface AcceptInviteRequest {
  invite_token: string;
  password: string;
}

export interface UserListItem {
  id: string;
  employee_id: string;
  name: string;
  email: string | null;
  role: UserRole;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface InviteRequest {
  email: string;
  name: string;
  employee_id: string;
  role: Exclude<UserRole, "platform_ops">;
}

export interface InviteResponse {
  user_id: string;
  invite_token: string;
  invite_url: string;
  expires_at: string;
}

export interface UserUpdateRequest {
  role?: UserRole;
  is_active?: boolean;
  name?: string;
}

export interface TenantDetailResponse extends TenantInfo {
  user_count: number;
  created_at: string;
}

export interface TenantUpdateRequest {
  name?: string;
  industry?: string;
  region?: string;
}

// ─── Token 管理 ──────────────────────────────────────────

const ACCESS_KEY = "cnc.access_token";
const REFRESH_KEY = "cnc.refresh_token";

export function saveTokens(t: TokenPair): void {
  localStorage.setItem(ACCESS_KEY, t.access_token);
  localStorage.setItem(REFRESH_KEY, t.refresh_token);
  // 清掉 dev 模式 fallback 的 X-Tenant-Id / X-User-Id, 走 JWT 路径
  localStorage.removeItem("cnc.tenant_id");
  localStorage.removeItem("cnc.user_id");
  localStorage.removeItem("cnc.role");
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function isLoggedIn(): boolean {
  return getAccessToken() !== null;
}

// ─── Auth endpoints ─────────────────────────────────────

/** 自助注册 · 建 tenant + admin · 立即返 JWT. */
export async function register(body: RegisterRequest): Promise<{
  user: UserInfo;
  tokens: TokenPair;
}> {
  const { data } = await apiClient.post("/v1/auth/register", body);
  saveTokens(data.tokens);
  return data;
}

/** 邮箱密码登录. */
export async function login(body: LoginRequest): Promise<TokenPair> {
  const { data } = await apiClient.post<TokenPair>("/v1/auth/login", body);
  saveTokens(data);
  return data;
}

/** 登出 · 后端记审计 + 前端清 token. */
export async function logout(): Promise<void> {
  try {
    await apiClient.post("/v1/auth/logout");
  } catch {
    // 即使后端 logout 失败也清前端 token
  } finally {
    clearTokens();
  }
}

/** 改密码 · 需 old + new. */
export async function changePassword(
  old_password: string,
  new_password: string,
): Promise<void> {
  await apiClient.post("/v1/auth/change-password", {
    old_password,
    new_password,
  });
}

/** refresh access token. */
export async function refreshAccessToken(): Promise<TokenPair | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  try {
    const { data } = await apiClient.post<TokenPair>("/v1/auth/refresh", {
      refresh_token: refresh,
    });
    saveTokens(data);
    return data;
  } catch {
    clearTokens();
    return null;
  }
}

/** 员工接受邀请 + 设密码 + 自动登录. */
export async function acceptInvite(body: AcceptInviteRequest): Promise<{
  user: UserInfo;
  tokens: TokenPair;
}> {
  const { data } = await apiClient.post("/v1/auth/accept-invite", body);
  saveTokens(data.tokens);
  return data;
}

/** 获取当前用户 + 所属 tenant. */
export async function getMe(): Promise<UserInfo> {
  const { data } = await apiClient.get<UserInfo>("/v1/auth/me");
  return data;
}

// ─── Admin endpoints ────────────────────────────────────

export async function listUsers(): Promise<{
  users: UserListItem[];
  total: number;
}> {
  const { data } = await apiClient.get("/v1/users", { _silentError: true });
  return data;
}

export async function inviteUser(body: InviteRequest): Promise<InviteResponse> {
  const { data } = await apiClient.post<InviteResponse>(
    "/v1/users/invite",
    body,
    { _silentError: true },
  );
  return data;
}

export async function updateUser(
  userId: string,
  body: UserUpdateRequest,
): Promise<void> {
  await apiClient.patch(`/v1/users/${userId}`, body, { _silentError: true });
}

export async function getMyTenant(): Promise<TenantDetailResponse> {
  const { data } = await apiClient.get<TenantDetailResponse>("/v1/tenant/me");
  return data;
}

export async function updateMyTenant(body: TenantUpdateRequest): Promise<void> {
  await apiClient.patch("/v1/tenant/me", body);
}
